import { useEffect, useState } from 'react';
import { useProfile } from './getQueries';

const usePermission = ({ study }) => {
  const [studyPermission, setStudyPermission] = useState({
    edit: false,
    transcriptEdit: false,
    preview: false,
    shareWithParticipants: false,
    addCollaborators: false,
    stopCollectingResponses: false,
    archive: false,
    deleteTranscript: false,
    deleteStudy: false,
    delete: false,
    deleteTranscripts: false,
    isUploaded: false,
  });
  const [analysisPermission, setAnalysisPermission] = useState({
    runAnalysis: false,
    reason: '',
  });
  const [isPending, setIsPending] = useState(true);

  const { data: user, isPending: userIsPending } = useProfile();

  useEffect(() => {
    if (userIsPending) {
      return;
    }
    if (study) {
      const isOwner = user?.id === study?.owner_id;
      const memberPermissions =
        study?.members?.find((member) => member?.user_email === user?.email)?.permissions || [];
      const hasEditPermission = memberPermissions.includes('edit');
      // Study-level admins are trusted with destructive ops on entities
      // INSIDE the study (transcripts, codes, tags...) but not with deleting
      // the study itself — that stays owner-only by design. Backend already
      // enforces this split via Permission.ADMIN in access_control/repository.
      const hasAdminPermission = memberPermissions.includes('admin');
      const canEditOrAdmin = hasEditPermission || hasAdminPermission;

      const isArchived = study?.status === 'archived' || false;
      const isInactive = study?.status === 'inactive' || false;
      const isDraft = study?.status === 'draft' || false;
      // Uploaded/secondary studies have is_primary === false.
      const isUploaded = study?.is_primary === false;
      const canManageEditableStudy = isOwner || (canEditOrAdmin && !isArchived);

      setStudyPermission({
        edit: canManageEditableStudy,
        transcriptEdit: canManageEditableStudy,
        preview: !isArchived && !isInactive && !isDraft && !isUploaded,
        shareWithParticipants: !isArchived && !isInactive && !isDraft && !isUploaded,
        addCollaborators: canManageEditableStudy,
        stopCollectingResponses: (isOwner || canEditOrAdmin) && !isUploaded,
        archive: isOwner || canEditOrAdmin,
        deleteTranscript: isOwner || hasAdminPermission,
        deleteStudy: isOwner,
        // Whole-study delete: owner only — admins are deliberately excluded.
        delete: isOwner,
        // Transcript-level delete: owner or any study admin. The backend
        // already accepts this (Permission.ADMIN at delete_qualitative_study_transcript);
        // the UI was just hiding the menu item for non-owners.
        deleteTranscripts: isOwner || hasAdminPermission,
        // Expose isUploaded flag for tooltip messages
        isUploaded,
      });
      setAnalysisPermission({
        runAnalysis: isOwner || canEditOrAdmin,
        reason:
          isOwner || canEditOrAdmin
            ? ''
            : 'Only the study owner or collaborators with edit or admin permission can run analysis.',
      });
    }
    setIsPending(false);
  }, [user, userIsPending, study]);

  return { data: { studyPermission, analysisPermission }, isPending };
};

export default usePermission;
